import { useMemo, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Employer, EmployerLedger } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function StaffContributions() {
  const { persona } = useAuth();
  const canManage = persona?.roles.some((r) => ['SPF_COMPLIANCE_OFFICER', 'SPF_ADMIN'].includes(r)) ?? false;
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Employer | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [showSweep, setShowSweep] = useState(false);
  const [showSubmitReturn, setShowSubmitReturn] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    return `/api/v1/contributions/employers?${p.toString()}`;
  }, [q]);
  const employersQ = useApiQuery<{ items: Employer[] }>(canManage ? query : null);
  const ledgerQ = useApiQuery<EmployerLedger>(canManage && selected ? `/api/v1/contributions/employers/${selected.id}/ledger` : null);

  if (!canManage) {
    return (
      <div>
        <PageHeader title="Contributions" subtitle="Employer accounts & contribution collection (P02)" />
        <ErrorNote message="Your role does not include SPF_COMPLIANCE_OFFICER or SPF_ADMIN — contribution collection is unavailable." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Contributions"
        subtitle="Employer accounts, monthly 5%+5% returns and arrears (P02)"
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowSweep(true)}>
              Run compliance sweep
            </button>
            <button className="btn-primary" onClick={() => setShowRegister(true)}>
              + Register employer
            </button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div>
          <input
            className="field-input mb-3"
            placeholder="Search employers (name/code/TIN)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {employersQ.error ? <ErrorNote message={employersQ.error} /> : null}
          <div className="card overflow-hidden">
            {employersQ.loading ? (
              <Spinner />
            ) : (employersQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No employers found" />
            ) : (
              <ul>
                {employersQ.data!.items.map((emp) => (
                  <li key={emp.id}>
                    <button
                      onClick={() => setSelected(emp)}
                      className={`block w-full border-b border-slate-100 px-3.5 py-2.5 text-left text-sm last:border-0 hover:bg-slate-50 ${
                        selected?.id === emp.id ? 'bg-brand-50' : ''
                      }`}
                    >
                      <div className="font-medium text-slate-800">{emp.name}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                        <span className="font-mono">{emp.code}</span>
                        <StatusBadge status={emp.status} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          {!selected ? (
            <div className="card p-8 text-center text-sm text-slate-400">Select an employer to view their ledger.</div>
          ) : ledgerQ.loading ? (
            <Spinner />
          ) : ledgerQ.error ? (
            <ErrorNote message={ledgerQ.error} />
          ) : ledgerQ.data ? (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-slate-900">{ledgerQ.data.employer.name}</h2>
                  <p className="text-xs text-slate-500">{ledgerQ.data.employer.code}</p>
                </div>
                <button className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setShowSubmitReturn(true)}>
                  + Submit return
                </button>
              </div>

              <div className="mb-4 grid gap-3 sm:grid-cols-2">
                <StatCard label="Open surcharges" value={scr(ledgerQ.data.openSurcharges)} tone={ledgerQ.data.openSurcharges > 0 ? 'warn' : 'good'} />
                <StatCard label="Returns on file" value={ledgerQ.data.returns.length} />
              </div>

              <h3 className="mb-2 text-sm font-bold text-slate-700">Contribution returns</h3>
              <div className="card mb-6 overflow-x-auto">
                {ledgerQ.data.returns.length === 0 ? (
                  <EmptyState title="No returns submitted yet" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">Period</th>
                        <th className="table-head text-right">Employee</th>
                        <th className="table-head text-right">Employer</th>
                        <th className="table-head">Status</th>
                        <th className="table-head" />
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerQ.data.returns.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="table-cell font-medium">{r.period}</td>
                          <td className="table-cell text-right tabular-nums">{scr(r.totals.employeeAmt)}</td>
                          <td className="table-cell text-right tabular-nums">{scr(r.totals.employerAmt)}</td>
                          <td className="table-cell">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="table-cell text-right">
                            {r.status === 'PAID' ? (
                              <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => postReturn(r.id, ledgerQ.reload)}>
                                Post to members
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <h3 className="mb-2 text-sm font-bold text-slate-700">Assessments (arrears)</h3>
              <div className="card overflow-x-auto">
                {ledgerQ.data.assessments.length === 0 ? (
                  <EmptyState title="No assessments raised" />
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="table-head">Period</th>
                        <th className="table-head">Type</th>
                        <th className="table-head text-right">Surcharge</th>
                        <th className="table-head">Status</th>
                        <th className="table-head" />
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerQ.data.assessments.map((a) => (
                        <tr key={a.id} className="hover:bg-slate-50">
                          <td className="table-cell">{a.period}</td>
                          <td className="table-cell text-xs text-slate-500">{a.type}</td>
                          <td className="table-cell text-right tabular-nums">{scr(a.surcharge)}</td>
                          <td className="table-cell">
                            <StatusBadge status={a.status} />
                          </td>
                          <td className="table-cell text-right">
                            {a.status === 'OPEN' ? (
                              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => settleAssessment(a.id, ledgerQ.reload)}>
                                Settle
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {showRegister ? (
        <RegisterEmployerModal
          onClose={() => setShowRegister(false)}
          onDone={() => {
            setShowRegister(false);
            employersQ.reload();
          }}
        />
      ) : null}

      {showSweep ? <ComplianceSweepModal onClose={() => setShowSweep(false)} /> : null}

      {showSubmitReturn && selected ? (
        <SubmitReturnModal
          employerCode={selected.code}
          onClose={() => setShowSubmitReturn(false)}
          onDone={() => {
            setShowSubmitReturn(false);
            ledgerQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

async function postReturn(id: string, reload: () => void) {
  try {
    await api.post(`/api/v1/contributions/returns/${id}/post`);
    reload();
  } catch {
    // surfaced via the ledger reload; keep the action simple like the reference pages
  }
}

async function settleAssessment(id: string, reload: () => void) {
  try {
    await api.post(`/api/v1/contributions/assessments/${id}/settle`);
    reload();
  } catch {
    // no-op; ledger reload reflects the current state either way
  }
}

function RegisterEmployerModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ code: '', name: '', tin: '', brn: '', sector: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/contributions/employers', {
        code: form.code,
        name: form.name,
        tin: form.tin,
        brn: form.brn || undefined,
        sector: form.sector,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register employer');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register an employer" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Employer code">
            <input className="field-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. HSN-2201" />
          </Field>
          <Field label="TIN">
            <input className="field-input" value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
          </Field>
        </div>
        <Field label="Employer name">
          <input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Business registration no. (optional)">
            <input className="field-input" value={form.brn} onChange={(e) => setForm({ ...form, brn: e.target.value })} />
          </Field>
          <Field label="Sector">
            <input className="field-input" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="e.g. Tourism" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.code || !form.name || !form.tin || !form.sector}>
            {busy ? 'Registering…' : 'Register employer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ComplianceSweepModal({ onClose }: { onClose: () => void }) {
  const [period, setPeriod] = useState('');
  const [surchargePct, setSurchargePct] = useState('10');
  const [result, setResult] = useState<{ period: string; raised: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ period: string; raised: number }>('/api/v1/contributions/compliance/run', {
        period,
        surchargePct: Number.parseFloat(surchargePct),
      });
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Compliance sweep failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Run compliance sweep" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Raises an automatic LATE assessment against every active employer with no paid/posted return for the period.
        </p>
        {error ? <ErrorNote message={error} /> : null}
        {result ? (
          <div className="rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">
            {result.raised} assessment{result.raised === 1 ? '' : 's'} raised for {result.period}.
          </div>
        ) : (
          <>
            <Field label="Period (YYYY-MM)">
              <input className="field-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07" />
            </Field>
            <Field label="Surcharge %">
              <input className="field-input" type="number" step="0.1" value={surchargePct} onChange={(e) => setSurchargePct(e.target.value)} />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result ? (
            <button className="btn-primary" onClick={submit} disabled={busy || !period}>
              {busy ? 'Running…' : 'Run sweep'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

interface ReturnLineForm {
  nin: string;
  grossWage: string;
  employeeAmt: string;
  employerAmt: string;
}

function SubmitReturnModal({ employerCode, onClose, onDone }: { employerCode: string; onClose: () => void; onDone: () => void }) {
  const [period, setPeriod] = useState('');
  const [lines, setLines] = useState<ReturnLineForm[]>([{ nin: '', grossWage: '', employeeAmt: '', employerAmt: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const updateLine = (i: number, patch: Partial<ReturnLineForm>) => {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/contributions/returns', {
        employerCode,
        period,
        lines: lines.map((l) => ({
          nin: l.nin,
          grossWage: Number.parseFloat(l.grossWage),
          employeeAmt: Number.parseFloat(l.employeeAmt),
          employerAmt: Number.parseFloat(l.employerAmt),
        })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit return');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Submit contribution return — ${employerCode}`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Period (YYYY-MM)">
          <input className="field-input max-w-[160px]" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07" />
        </Field>

        <div className="space-y-2">
          <span className="field-label">Employee lines</span>
          {lines.map((line, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2">
              <input className="field-input" placeholder="NIN" value={line.nin} onChange={(e) => updateLine(i, { nin: e.target.value })} />
              <input
                className="field-input"
                type="number"
                placeholder="Gross wage"
                value={line.grossWage}
                onChange={(e) => updateLine(i, { grossWage: e.target.value })}
              />
              <input
                className="field-input"
                type="number"
                placeholder="Employee 5%"
                value={line.employeeAmt}
                onChange={(e) => updateLine(i, { employeeAmt: e.target.value })}
              />
              <input
                className="field-input"
                type="number"
                placeholder="Employer 5%"
                value={line.employerAmt}
                onChange={(e) => updateLine(i, { employerAmt: e.target.value })}
              />
              <button
                className="btn-secondary !px-2 text-xs"
                onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
                disabled={lines.length === 1}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            className="btn-secondary text-xs"
            onClick={() => setLines((ls) => [...ls, { nin: '', grossWage: '', employeeAmt: '', employerAmt: '' }])}
          >
            + Add line
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !period || lines.some((l) => !l.nin || !l.grossWage || !l.employeeAmt || !l.employerAmt)}
          >
            {busy ? 'Submitting…' : 'Submit return'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
